import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Image, Typography, Spin, Empty, Modal, Descriptions, Button, Space, Row, Col } from 'antd';
import { EnvironmentOutlined, CloseOutlined, LeftOutlined, RightOutlined, FileImageOutlined } from '@ant-design/icons';
import { secureLogger } from '../utils/secureLogger';
import { API_CONFIG } from '../config/api';
import { useDevice } from '../contexts/DeviceContext';
import { fetchSignedMediaUrl } from '../utils/media';
import PhotoLocationMap from './PhotoLocationMap';

const { Text } = Typography;
const COLUMN_WIDTH_STEP = 12;
const TIME_ZONE = 'Asia/Tokyo';
const DATE_KEY_FORMATTER = new Intl.DateTimeFormat('sv-SE', {
  timeZone: TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const TIMELINE_CURSOR_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">' +
  '<line x1="3" y1="10" x2="17" y2="10" stroke="#0f0f0f" stroke-width="1.5" />' +
  '<path d="M10 2 L6.5 6 L13.5 6 Z" fill="#0f0f0f" />' +
  '<path d="M10 18 L6.5 14 L13.5 14 Z" fill="#0f0f0f" />' +
  '</svg>';
const TIMELINE_CURSOR = `url("data:image/svg+xml;utf8,${encodeURIComponent(
  TIMELINE_CURSOR_SVG,
)}") 10 10, ns-resize`;

const toDateSafe = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toDateKey = (date: Date) => DATE_KEY_FORMATTER.format(date);
const toMonthKey = (date: Date) => toDateKey(date).slice(0, 7);
const formatDateLabel = (date: Date) =>
  date.toLocaleDateString('ja-JP', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
const formatDateTimeLabel = (date: Date) =>
  date.toLocaleString('ja-JP', { timeZone: TIME_ZONE });
const formatTimeLabel = (date: Date) =>
  date.toLocaleTimeString('ja-JP', {
    timeZone: TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  });
const normalizeMonthKey = (value: string) => (value.length > 7 ? value.slice(0, 7) : value);
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const getTimeZoneOffsetMs = (date: Date) => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value]),
  );
  const utcTime = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second),
  );
  return utcTime - date.getTime();
};

const getMonthRangeUtc = (monthKey: string) => {
  const [year, month] = monthKey.split('-').map((part) => Number(part));
  const startLocalUtc = Date.UTC(year, month - 1, 1, 0, 0, 0);
  const endLocalUtc = Date.UTC(year, month, 1, 0, 0, 0);
  const startOffset = getTimeZoneOffsetMs(new Date(startLocalUtc));
  const endOffset = getTimeZoneOffsetMs(new Date(endLocalUtc));
  const start = new Date(startLocalUtc - startOffset);
  const end = new Date(endLocalUtc - endOffset);
  return { start, end };
};
interface PhotoData {
  id: string;
  captured_at: string;
  file_size: number;
  status: string;
  image_width?: number;
  image_height?: number;
  camera_make?: string;
  camera_model?: string;
  gps_latitude?: number;
  gps_longitude?: number;
  imu_orientation?: number;
  thumbnailUrl?: string;
  // 後方互換性のため
  image_data?: string;
  timestamp?: string;
  latitude?: number;
  longitude?: number;
  direction?: number;
}

interface PhotoDetail extends PhotoData {
  orientation?: number;
  camera_software?: string;
  aperture_value?: number;
  exposure_time?: number;
  iso_speed?: number;
  focal_length?: number;
  focal_length_35mm?: number;
  white_balance?: number;
  flash?: number;
  gps_altitude?: number;
  imu_pitch?: number;
  imu_roll?: number;
  exif_data?: Record<string, any>;
}

interface FaceDetectionFace {
  face_photo_id?: string;
  image_url?: string;
  score?: number;
  bbox?: number[];
  landmarks?: number[][];
  embedding_dim?: number;
  embedding_available?: boolean;
}

interface FaceDetectionStatus {
  job_id: string;
  status: string;
  progress?: number;
  error_message?: string;
  output_bucket?: string;
  output_prefix?: string;
  metadata_bucket?: string;
  metadata_key?: string;
  started_at?: string;
  completed_at?: string;
  faces?: FaceDetectionFace[];
}

interface PhotoGalleryProps {
  photoData?: PhotoData[];
  total?: number;
  hasMore?: boolean;
  hasMorePrev?: boolean;
  loadingMore?: boolean;
  loadingPrev?: boolean;
  loadingInitial?: boolean;
  onLoadMore?: () => void;
  onLoadPrev?: () => void;
  onTimelineSelect?: (isoTimestamp: string) => void;
  selectedTimelineAt?: string | null;
  scrollTarget?: string | null;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  columnWidth?: number;
  onColumnWidthChange?: (nextWidth: number) => void;
  timeline?: {
    min_date?: string | null;
    max_date?: string | null;
    months: Array<{ month: string; count: number }>;
    days?: Array<{ date: string; count: number }>;
  } | null;
}


const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  photoData,
  total,
  hasMore,
  hasMorePrev,
  loadingMore,
  loadingPrev,
  loadingInitial,
  onLoadMore,
  onLoadPrev,
  onTimelineSelect,
  selectedTimelineAt,
  scrollTarget,
  scrollContainerRef: externalScrollContainerRef,
  columnWidth = 180,
  onColumnWidthChange,
  timeline,
}) => {
  const { selectedDevice } = useDevice();
  const [photos, setPhotos] = useState<PhotoData[]>([]);
  const [loading, setLoading] = useState(false);
  const [failedThumbnails, setFailedThumbnails] = useState<Set<string>>(new Set());
  const thumbnailCacheRef = useRef<Map<string, string>>(new Map());
  const loadPrevRef = useRef<HTMLDivElement | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const wheelLoadPrevRef = useRef(false);
  const wheelLoadMoreRef = useRef(false);
  const loadMoreVisibleRef = useRef(false);
  const pendingLoadMoreRef = useRef(false);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const timelineContainerRef = useRef<HTMLDivElement | null>(null);
  const userScrolledRef = useRef(false);
  const [timelineHeight, setTimelineHeight] = useState<number | null>(null);
  const autoFillTriggeredLengthRef = useRef<number>(-1);
  const [timelineIndicator, setTimelineIndicator] = useState<{
    offset: number;
    label: string;
    isActive: boolean;
  } | null>(null);
  const lastScrollTopRef = useRef(0);
  const [internalSelectedAt, setInternalSelectedAt] = useState<string | null>(null);
  const lastScrollTargetRef = useRef<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [photoDetail, setPhotoDetail] = useState<PhotoDetail | null>(null);
  const internalScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = externalScrollContainerRef ?? internalScrollContainerRef;
  const [detailLoading, setDetailLoading] = useState(false);
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [faceDetection, setFaceDetection] = useState<FaceDetectionStatus | null>(null);
  const [faceDetectionLoading, setFaceDetectionLoading] = useState(false);
  const [faceDetectionError, setFaceDetectionError] = useState<string | null>(null);
  const [faceDetectionSubmitting, setFaceDetectionSubmitting] = useState(false);
  const canLoadMore = Boolean(onLoadMore && hasMore);
  const canLoadPrev = Boolean(onLoadPrev && hasMorePrev);
  const effectiveSelectedAt = internalSelectedAt ?? selectedTimelineAt;
  const isInitialLoading = Boolean(loadingInitial || loading);

  useEffect(() => {
    setFailedThumbnails(new Set());
  }, [selectedDevice?.id, photoData]);

  useEffect(() => {
    if (photoData) {
      loadThumbnails(photoData);
    } else if (selectedDevice) {
      loadPhotos();
    }
    
    return () => {
      if (fullImageUrl && fullImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fullImageUrl);
      }
    };
  }, [photoData, selectedDevice]);

  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }
    const container = scrollContainerRef.current;
    const handleScroll = () => {
      userScrolledRef.current = true;
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    userScrolledRef.current = false;
  }, [selectedTimelineAt]);

  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }
    const container = scrollContainerRef.current;
    const updateLayout = () => {
      const height = container.clientHeight;
      if (height > 0) {
        setTimelineHeight((prev) => (prev === height ? prev : height));
      }
      if (!onLoadMore || !hasMore || loading || loadingMore) {
        return;
      }
      const short = container.scrollHeight <= container.clientHeight + 40;
      if (short && autoFillTriggeredLengthRef.current !== photos.length) {
        autoFillTriggeredLengthRef.current = photos.length;
        onLoadMore();
      }
    };
    const observer = new ResizeObserver(updateLayout);
    observer.observe(container);
    updateLayout();
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, loading, loadingMore, photos.length]);

  useEffect(() => {
    if (!scrollContainerRef.current || !loadMoreRef.current || !onLoadMore) {
      return;
    }
    const root = scrollContainerRef.current;
    const target = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        loadMoreVisibleRef.current = isVisible;
        if (!isVisible) {
          return;
        }
        if (!canLoadMore || loading || loadingMore) {
          pendingLoadMoreRef.current = true;
          return;
        }
        pendingLoadMoreRef.current = false;
        onLoadMore();
      },
      { root, rootMargin: '200px 0px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [onLoadMore, canLoadMore, loading, loadingMore]);

  useEffect(() => {
    if (!onLoadMore || !canLoadMore || loading || loadingMore) {
      return;
    }
    if (!loadMoreVisibleRef.current || !pendingLoadMoreRef.current) {
      return;
    }
    pendingLoadMoreRef.current = false;
    onLoadMore();
  }, [onLoadMore, canLoadMore, loading, loadingMore]);

  useEffect(() => {
    if (!onLoadMore || !canLoadMore || loading || loadingMore) {
      return;
    }
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const distance = container.scrollHeight - (container.scrollTop + container.clientHeight);
    if (distance > 40) {
      return;
    }
    if (autoFillTriggeredLengthRef.current === photos.length) {
      return;
    }
    autoFillTriggeredLengthRef.current = photos.length;
    onLoadMore();
  }, [onLoadMore, canLoadMore, loading, loadingMore, photos.length, scrollContainerRef]);

  useEffect(() => {
    if (!scrollContainerRef.current || !onLoadPrev) {
      return;
    }
    const container = scrollContainerRef.current;
    const handleWheel = (event: WheelEvent) => {
      if (event.shiftKey) {
        return;
      }
      if (event.deltaY < 0) {
        if (!hasMorePrev || loading || loadingMore || loadingPrev) {
          return;
        }
        if (container.scrollTop > 2) {
          return;
        }
        if (wheelLoadPrevRef.current) {
          return;
        }
        wheelLoadPrevRef.current = true;
        onLoadPrev();
        return;
      }
      if (event.deltaY > 0) {
        if (!hasMore || !onLoadMore || loading || loadingMore || loadingPrev) {
          return;
        }
        const distance = container.scrollHeight - (container.scrollTop + container.clientHeight);
        if (distance > 4) {
          return;
        }
        if (wheelLoadMoreRef.current) {
          return;
        }
        wheelLoadMoreRef.current = true;
        onLoadMore();
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [onLoadPrev, onLoadMore, hasMorePrev, hasMore, loading, loadingMore, loadingPrev, scrollContainerRef]);

  useEffect(() => {
    if (!loadingPrev) {
      wheelLoadPrevRef.current = false;
    }
  }, [loadingPrev]);

  useEffect(() => {
    if (!loadingMore) {
      wheelLoadMoreRef.current = false;
    }
  }, [loadingMore]);

  const loadThumbnails = async (photoList: PhotoData[]) => {
    setLoading(true);
    try {
      const scrollContainer = scrollContainerRef.current;
      const prevScroll = scrollContainer
        ? { top: scrollContainer.scrollTop, height: scrollContainer.scrollHeight }
        : null;
      const anchor = (() => {
        if (!scrollContainer || !userScrolledRef.current) {
          return null;
        }
        const containerRect = scrollContainer.getBoundingClientRect();
        const items = Array.from(scrollContainer.querySelectorAll<HTMLElement>('[data-photo-id]'));
        let candidate: { id: string; distance: number } | null = null;
        for (const item of items) {
          const rect = item.getBoundingClientRect();
          if (rect.bottom <= containerRect.top) {
            continue;
          }
          if (rect.top >= containerRect.bottom) {
            continue;
          }
          const id = item.dataset.photoId;
          if (!id) {
            continue;
          }
          const distance = rect.top - containerRect.top;
          if (!candidate || distance < candidate.distance) {
            candidate = { id, distance };
          }
        }
        return candidate;
      })();
      const photosWithThumbnails = photoList.map((photo: PhotoData) => {
        const cached = thumbnailCacheRef.current.get(photo.id);
        const thumbnailUrl = photo.thumbnailUrl ?? cached ?? null;
        if (thumbnailUrl) {
          thumbnailCacheRef.current.set(photo.id, thumbnailUrl);
        }
        return { ...photo, thumbnailUrl: thumbnailUrl ?? undefined };
      });
      setPhotos(photosWithThumbnails);
      if (prevScroll) {
        requestAnimationFrame(() => {
          const nextContainer = scrollContainerRef.current;
          if (!nextContainer) {
            return;
          }
          if (anchor) {
            const anchorEl = nextContainer.querySelector<HTMLElement>(`[data-photo-id="${anchor.id}"]`);
            if (anchorEl) {
              const containerRect = nextContainer.getBoundingClientRect();
              const nextDistance = anchorEl.getBoundingClientRect().top - containerRect.top;
              const delta = nextDistance - anchor.distance;
              if (Math.abs(delta) > 0.5) {
                nextContainer.scrollTop = Math.max(0, nextContainer.scrollTop + delta);
              }
              return;
            }
          }
          const nextHeight = nextContainer.scrollHeight;
          if (nextHeight < prevScroll.height && userScrolledRef.current) {
            const delta = nextHeight - prevScroll.height;
            nextContainer.scrollTop = Math.max(0, prevScroll.top + delta);
          }
        });
      }
    } catch (error) {
      secureLogger.error('Failed to load thumbnails', { error: 'Thumbnail request failed' });
    } finally {
      setLoading(false);
    }
  };

  const markThumbnailFailed = useCallback((photoId: string) => {
    setFailedThumbnails((prev) => {
      if (prev.has(photoId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(photoId);
      return next;
    });
  }, []);

  const normalizeImageUrl = (value?: string) => {
    if (!value) {
      return null;
    }
    if (value.includes('base64,undefined') || value.includes('base64,null')) {
      return null;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
      return null;
    }
    if (trimmed.startsWith('data:image/')) {
      return null;
    }
    return trimmed;
  };

  const loadPhotos = async () => {
    if (!selectedDevice) return;
    
    setLoading(true);
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        secureLogger.error('No token for photos request');
        return;
      }
      
      const now = new Date();
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const end = now.toISOString();
      
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos?from_time=${start}&to_time=${end}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        loadThumbnails(data.photos || []);
      } else {
        secureLogger.error('Failed to load photos - HTTP error', { 
          status: response.status, 
          statusText: response.statusText 
        });
      }
    } catch (error) {
      secureLogger.error('Failed to load photos - Network error', { 
        error: 'Network request failed' 
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = toDateSafe(timestamp);
    return date ? formatDateTimeLabel(date) : '';
  };

  const formatTimeOnly = (timestamp: string) => {
    const date = toDateSafe(timestamp);
    return date ? formatTimeLabel(date) : '';
  };

  const toGroupKey = useCallback((iso: string) => {
    const date = toDateSafe(iso);
    return date ? toDateKey(date) : '';
  }, []);

  const groupedPhotos = useMemo(() => {
    const map = new Map<string, { label: string; items: PhotoData[] }>();
    photos.forEach((photo) => {
      const raw = photo.captured_at || photo.timestamp;
      const date = toDateSafe(raw);
      if (!date) {
        return;
      }
      const key = toDateKey(date);
      const label = formatDateLabel(date);
      if (!map.has(key)) {
        map.set(key, { label, items: [] });
      }
      map.get(key)!.items.push(photo);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .map(([key, value]) => ({
        key,
        label: value.label,
        items: value.items.sort((a, b) => {
          const aTime = new Date((a.captured_at || a.timestamp) ?? 0).getTime();
          const bTime = new Date((b.captured_at || b.timestamp) ?? 0).getTime();
          return bTime - aTime;
        }),
      }));
  }, [photos]);

  const photoTimeMap = useMemo(() => {
    const map = new Map<string, number>();
    photos.forEach((photo) => {
      const raw = photo.captured_at || photo.timestamp;
      const date = toDateSafe(raw);
      if (date) {
        map.set(photo.id, date.getTime());
      }
    });
    return map;
  }, [photos]);

  const timelineScale = useMemo(() => {
    if (!timeline?.min_date || !timeline?.max_date) {
      return null;
    }
    const start = toDateSafe(timeline.min_date);
    const end = toDateSafe(timeline.max_date);
    if (!start || !end) {
      return null;
    }
    const startKey = toMonthKey(start);
    const endKey = toMonthKey(end);
    const [startYear, startMonthNumber] = startKey.split('-').map((part) => Number(part));
    const [endYear, endMonthNumber] = endKey.split('-').map((part) => Number(part));
    if (
      !Number.isFinite(startYear) ||
      !Number.isFinite(startMonthNumber) ||
      !Number.isFinite(endYear) ||
      !Number.isFinite(endMonthNumber)
    ) {
      return null;
    }
    const months: Array<{ key: string; label: string; count: number }> = [];
    const monthCounts = new Map<string, number>(
      timeline.months.map((item) => [
        normalizeMonthKey(item.month),
        Number(item.count) || 0,
      ]),
    );

    let cursorYear = startYear;
    let cursorMonth = startMonthNumber;
    while (cursorYear < endYear || (cursorYear === endYear && cursorMonth <= endMonthNumber)) {
      const key = `${cursorYear}-${String(cursorMonth).padStart(2, '0')}`;
      const label = `${cursorYear}年${cursorMonth}月`;
      months.push({ key, label, count: monthCounts.get(key) ?? 0 });
      cursorMonth += 1;
      if (cursorMonth > 12) {
        cursorMonth = 1;
        cursorYear += 1;
      }
    }

    const orderedMonths = months.slice().reverse();
    const yearMarkers: Array<{ label: string; ratio: number }> = [];
    const quarterMarkers: Array<{ label: string; ratio: number }> = [];
    const seenYears = new Set<string>();
    const seenQuarters = new Set<string>();
    const total = orderedMonths.length;

    orderedMonths.forEach((item, index) => {
      const year = item.key.slice(0, 4);
      const monthNumber = Number(item.key.slice(5, 7));
      const quarter = Math.floor((monthNumber - 1) / 3) + 1;
      const ratio = total > 1 ? index / (total - 1) : 0;

      if (!seenYears.has(year)) {
        seenYears.add(year);
        yearMarkers.push({ label: year, ratio });
      }

      const quarterKey = `${year}-Q${quarter}`;
      if (!seenQuarters.has(quarterKey)) {
        seenQuarters.add(quarterKey);
        quarterMarkers.push({ label: `Q${quarter}`, ratio });
      }
    });

    return {
      start,
      end,
      months: orderedMonths,
      yearMarkers,
      quarterMarkers,
    };
  }, [timeline]);

  const timelineDayCounts = useMemo(() => {
    if (!timeline?.days || timeline.days.length === 0) {
      return null;
    }
    return new Map<string, number>(
      timeline.days.map((day) => [day.date, Number(day.count) || 0]),
    );
  }, [timeline]);

  const isTimelineDayActive = useCallback(
    (iso: string) => {
      if (!timelineDayCounts) {
        return true;
      }
      const date = toDateSafe(iso);
      if (!date) {
        return false;
      }
      const key = toDateKey(date);
      return (timelineDayCounts.get(key) ?? 0) > 0;
    },
    [timelineDayCounts],
  );

  const getTimelineMonthBounds = useCallback(
    (monthKey: string) => {
      const base = getMonthRangeUtc(monthKey);
      if (!timelineScale) {
        return base;
      }
      const minDate = timelineScale.start;
      const maxDate = timelineScale.end;
      const minKey = toMonthKey(minDate);
      const maxKey = toMonthKey(maxDate);
      let start = base.start;
      let end = base.end;
      if (monthKey === maxKey) {
        end = maxDate;
      }
      if (monthKey === minKey) {
        start = minDate;
      }
      if (start.getTime() > end.getTime()) {
        start = minDate;
        end = maxDate;
      }
      return { start, end };
    },
    [timelineScale],
  );

  const getIndicatorForDate = useCallback(
    (iso: string) => {
      if (!timelineScale || !timelineContainerRef.current) {
        return null;
      }
      const rect = timelineContainerRef.current.getBoundingClientRect();
      const targetDate = new Date(iso);
      if (!Number.isFinite(targetDate.getTime())) {
        return null;
      }
      const totalMonths = timelineScale.months.length;
      if (totalMonths === 0) {
        return null;
      }
      const monthKey = toMonthKey(targetDate);
      const index = timelineScale.months.findIndex((month) => month.key === monthKey);
      if (index < 0) {
        return null;
      }
      const { start, end } = getTimelineMonthBounds(monthKey);
      const span = end.getTime() - start.getTime();
      const fraction =
        span > 0 ? clamp((targetDate.getTime() - start.getTime()) / span, 0, 1) : 0;
      const invertedFraction = 1 - fraction;
      const ratioWithInvert =
        totalMonths > 1 ? (index + clamp(invertedFraction, 0, 1)) / (totalMonths - 1) : 0;
      const offset = rect.height * clamp(ratioWithInvert, 0, 1);
      const label = formatDateLabel(targetDate);
      return {
        offset,
        label,
        isActive: isTimelineDayActive(iso),
      };
    },
    [timelineScale, getTimelineMonthBounds, isTimelineDayActive],
  );

  const setGroupRef = useCallback((key: string) => {
    return (node: HTMLDivElement | null) => {
      groupRefs.current[key] = node;
    };
  }, []);

  const monthToGroupKey = useMemo(() => {
    const map = new Map<string, string>();
    groupedPhotos.forEach((group) => {
      const monthKey = group.key.slice(0, 7);
      if (!map.has(monthKey)) {
        map.set(monthKey, group.key);
      }
    });
    return map;
  }, [groupedPhotos]);

  const getTimelineTargetAtRatio = useCallback(
    (ratio: number) => {
      if (!timelineScale) {
        return null;
      }
      const totalMonths = timelineScale.months.length;
      if (totalMonths === 0) {
        return null;
      }
      const clampedRatio = clamp(ratio, 0, 1);
      const rawIndex = clampedRatio * (totalMonths - 1);
      const baseIndex = Math.min(totalMonths - 1, Math.floor(rawIndex));
      const month = timelineScale.months[baseIndex];
      const { start, end } = getTimelineMonthBounds(month.key);
      const fraction = rawIndex - baseIndex;
      const span = end.getTime() - start.getTime();
      if (span <= 0) {
        return new Date(start.getTime()).toISOString();
      }
      const endInclusive = end.getTime() - 1;
      const spanInclusive = endInclusive - start.getTime();
      const targetMs = endInclusive - spanInclusive * clamp(fraction, 0, 1);
      return new Date(targetMs).toISOString();
    },
    [timelineScale, getTimelineMonthBounds],
  );

  const getTimelineTargetAt = useCallback(
    (clientY: number) => {
      if (!timelineScale || !timelineContainerRef.current) {
        return null;
      }
      const rect = timelineContainerRef.current.getBoundingClientRect();
      const offset = Math.min(Math.max(clientY - rect.top, 0), rect.height);
      const ratio = rect.height > 0 ? offset / rect.height : 0;
      return getTimelineTargetAtRatio(ratio);
    },
    [timelineScale, getTimelineTargetAtRatio],
  );

  const handleTimelineClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const at = getTimelineTargetAt(event.clientY);
      if (!at) {
        return;
      }
      if (!isTimelineDayActive(at)) {
        return;
      }
      setInternalSelectedAt(at);
      if (onTimelineSelect) {
        onTimelineSelect(at);
        return;
      }
      const groupKey = monthToGroupKey.get(at.slice(0, 7)) ?? toGroupKey(at);
      const target = groupRefs.current[groupKey];
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [getTimelineTargetAt, isTimelineDayActive, onTimelineSelect, monthToGroupKey, toGroupKey],
  );

  useEffect(() => {
    if (!scrollContainerRef.current) {
      return;
    }
    const container = scrollContainerRef.current;
    let rafId = 0;
    const handleScroll = () => {
      userScrolledRef.current = true;
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        if (loading || loadingMore || loadingPrev) {
          return;
        }
        const currentTop = container.scrollTop;
        const isScrollingDown = currentTop > lastScrollTopRef.current;
        const isScrollingUp = currentTop < lastScrollTopRef.current;
        lastScrollTopRef.current = currentTop;
        if (hasMore && onLoadMore) {
          const distance = container.scrollHeight - (container.scrollTop + container.clientHeight);
          if (distance < 400 && isScrollingDown) {
            onLoadMore();
          }
        }
        if (hasMorePrev && onLoadPrev) {
          if (container.scrollTop < 200 && isScrollingUp) {
            onLoadPrev();
          }
        }
      });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      container.removeEventListener('scroll', handleScroll);
    };
  }, [onLoadMore, onLoadPrev, hasMore, hasMorePrev, loading, loadingMore, loadingPrev]);

  useEffect(() => {
    if (groupedPhotos.length === 0) {
      setActiveGroupKey(null);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const key = visible[0].target.getAttribute('data-date-key');
          if (key) {
            setActiveGroupKey(key);
          }
        }
      },
      { root: scrollContainerRef.current, rootMargin: '-20% 0px -70% 0px', threshold: [0.1, 0.6, 1] },
    );

    Object.values(groupRefs.current).forEach((node) => {
      if (node) {
        observer.observe(node);
      }
    });

    return () => observer.disconnect();
  }, [groupedPhotos]);

  useEffect(() => {
    if (!scrollContainerRef.current || photos.length === 0) {
      return;
    }
    const container = scrollContainerRef.current;
    let rafId = 0;
    const handleScroll = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        const containerRect = container.getBoundingClientRect();
        const anchor = containerRect.top + Math.max(container.clientHeight * 0.3, 8);
        const items = Array.from(container.querySelectorAll<HTMLElement>('[data-photo-id]'));
        let above: { top: number; time: number } | null = null;
        let below: { top: number; time: number } | null = null;
        items.forEach((item) => {
          const id = item.dataset.photoId;
          if (!id) {
            return;
          }
          const time = photoTimeMap.get(id);
          if (!time) {
            return;
          }
          const rect = item.getBoundingClientRect();
          const top = rect.top;
          if (top <= anchor) {
            if (!above || top > above.top) {
              above = { top, time };
            }
          }
          if (top >= anchor) {
            if (!below || top < below.top) {
              below = { top, time };
            }
          }
        });
        if (!above && !below) {
          return;
        }
        let targetTime = (above ?? below)!.time;
        if (above && below && below.top !== above.top) {
          const ratio = clamp((anchor - above.top) / (below.top - above.top), 0, 1);
          targetTime = above.time + (below.time - above.time) * ratio;
        }
        setInternalSelectedAt(new Date(targetTime).toISOString());
      });
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      container.removeEventListener('scroll', handleScroll);
    };
  }, [scrollContainerRef, photos.length, photoTimeMap]);

  const handleTimelineMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!timelineScale || !timelineContainerRef.current) {
        return;
      }
      const rect = timelineContainerRef.current.getBoundingClientRect();
      const offset = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
      const ratio = rect.height > 0 ? offset / rect.height : 0;
      const iso = getTimelineTargetAtRatio(ratio);
      if (!iso) {
        return;
      }
      const targetDate = new Date(iso);
      const label = formatDateLabel(targetDate);
      setTimelineIndicator({
        offset,
        label,
        isActive: isTimelineDayActive(iso),
      });
    },
    [timelineScale, getTimelineTargetAtRatio, isTimelineDayActive],
  );

  const handleTimelineMouseLeave = useCallback(() => {}, []);

  useEffect(() => {
    if (!timelineScale) {
      setTimelineIndicator(null);
      return;
    }
    if (!effectiveSelectedAt) {
      return;
    }
    const indicator = getIndicatorForDate(effectiveSelectedAt);
    if (indicator) {
      setTimelineIndicator(indicator);
    }
  }, [timelineScale, effectiveSelectedAt, getIndicatorForDate]);

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (!event.shiftKey) {
        if (!onLoadMore || !hasMore || loading || loadingMore || loadingPrev) {
          return;
        }
        if (event.deltaY <= 0) {
          return;
        }
        const container = scrollContainerRef.current;
        if (!container) {
          return;
        }
        const distance = container.scrollHeight - (container.scrollTop + container.clientHeight);
        if (distance > 4) {
          return;
        }
        if (wheelLoadMoreRef.current) {
          return;
        }
        wheelLoadMoreRef.current = true;
        onLoadMore();
        return;
      }
      if (!onColumnWidthChange) {
        return;
      }
      event.preventDefault();
      const direction = event.deltaY > 0 ? -1 : 1;
      onColumnWidthChange(columnWidth + direction * COLUMN_WIDTH_STEP);
    },
    [
      columnWidth,
      onColumnWidthChange,
      onLoadMore,
      hasMore,
      loading,
      loadingMore,
      loadingPrev,
      scrollContainerRef,
    ],
  );

  useEffect(() => {
    if (!scrollTarget) {
      return;
    }
    const key = toGroupKey(scrollTarget);
    const fallbackKey = monthToGroupKey.get(key.slice(0, 7));
    const target = groupRefs.current[key] ?? (fallbackKey ? groupRefs.current[fallbackKey] : null);
    if (target) {
      if (lastScrollTargetRef.current === scrollTarget) {
        return;
      }
      lastScrollTargetRef.current = scrollTarget;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [scrollTarget, toGroupKey, groupedPhotos, monthToGroupKey]);

  const fetchFaceDetectionStatus = async (photoId: string, token: string) => {
    if (!selectedDevice) return;
    setFaceDetectionLoading(true);
    setFaceDetectionError(null);
    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos/${photoId}/face-detection`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.status === 404) {
        setFaceDetection(null);
        return;
      }
      if (!response.ok) {
        setFaceDetectionError('顔検出情報の取得に失敗しました');
        return;
      }
      const data = await response.json();
      setFaceDetection(data);
    } catch (error) {
      secureLogger.error('Failed to load face detection status', { photoId });
      setFaceDetectionError('顔検出情報の取得に失敗しました');
    } finally {
      setFaceDetectionLoading(false);
    }
  };

  const handleFaceDetectionRetry = async () => {
    if (!selectedDevice || !selectedPhoto) return;
    setFaceDetectionSubmitting(true);
    setFaceDetectionError(null);
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) {
        secureLogger.error('No token for face detection retry');
        return;
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos/${selectedPhoto}/face-detection/retry`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) {
        setFaceDetectionError('顔検出の再実行に失敗しました');
        return;
      }
      const data = await response.json();
      setFaceDetection(data);
    } catch (error) {
      secureLogger.error('Failed to retry face detection', { photoId: selectedPhoto });
      setFaceDetectionError('顔検出の再実行に失敗しました');
    } finally {
      setFaceDetectionSubmitting(false);
    }
  };

  const handlePhotoClick = async (photoId: string) => {
    if (!selectedDevice) return;
    
    setSelectedPhoto(photoId);
    setDetailLoading(true);
    setPhotoDetail(null);
    setFullImageUrl(null);
    setFaceDetection(null);
    setFaceDetectionError(null);
    
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        secureLogger.error('No token for photo detail request');
        return;
      }
      
      // 詳細情報とフル画像を並行取得
      const [detailResponse, signedUrl] = await Promise.all([
        fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${selectedDevice.id}/photos/${photoId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetchSignedMediaUrl(`/api/v1/devices/${selectedDevice.id}/photos/${photoId}/file`, token),
      ]);
      await fetchFaceDetectionStatus(photoId, token);
      
      if (detailResponse.ok) {
        const detail = await detailResponse.json();
        setPhotoDetail(detail);
      }
      
      if (signedUrl) {
        const imageResponse = await fetch(signedUrl);
        if (imageResponse.ok) {
          const blob = await imageResponse.blob();
          const imageUrl = URL.createObjectURL(blob);
          setFullImageUrl(imageUrl);
        } else {
          secureLogger.error('Failed to download photo via signed URL', { photoId });
        }
      }
    } catch (error) {
      secureLogger.error('Failed to load photo detail', { photoId });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleModalClose = () => {
    setSelectedPhoto(null);
    setPhotoDetail(null);
    setFaceDetection(null);
    setFaceDetectionError(null);
    if (fullImageUrl && fullImageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fullImageUrl);
    }
    setFullImageUrl(null);
  };

  const currentIndex = selectedPhoto
    ? photos.findIndex((photo) => photo.id === selectedPhoto)
    : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < photos.length - 1;

  const handlePrev = () => {
    if (!hasPrev) return;
    const target = photos[currentIndex - 1];
    if (target) {
      handlePhotoClick(target.id);
    }
  };

  const handleNext = () => {
    if (!hasNext) return;
    const target = photos[currentIndex + 1];
    if (target) {
      handlePhotoClick(target.id);
    }
  };

  const faceStatusLabel = (status?: string | null) => {
    switch (status) {
      case 'queued':
        return 'キュー待ち';
      case 'running':
        return '処理中';
      case 'succeeded':
        return '完了';
      case 'failed':
        return '失敗';
      default:
        return '未実行';
    }
  };

  const isFaceDetectionRunning =
    faceDetection?.status === 'queued' || faceDetection?.status === 'running';

  const embeddingDim =
    faceDetection?.faces?.find((face) => typeof face.embedding_dim === 'number')?.embedding_dim ?? null;

  if (isInitialLoading && photos.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>写真を読み込み中...</div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="写真データがありません"
      />
    );
  }

  return (
    <div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', gap: 16, height: '100%', minHeight: 0 }}>
        <div
          ref={scrollContainerRef}
          onWheel={handleWheel}
          style={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: 'auto',
            height: '100%',
            paddingRight: 4,
          }}
        >
          <div ref={loadPrevRef} style={{ padding: '12px 0', textAlign: 'center' }}>
            {(loadingPrev || loading) && canLoadPrev && <Spin size="small" />}
            {canLoadPrev && !(loadingPrev || loading) && (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>上へスクロールで読み込み</div>
            )}
          </div>
          {groupedPhotos.map((group) => (
            <div
              key={group.key}
              style={{
                marginBottom: 24,
                contentVisibility: 'auto',
                containIntrinsicSize: '800px',
              }}
            >
              <div
                ref={setGroupRef(group.key)}
                data-date-key={group.key}
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 12,
                  color: '#1f1f1f',
                }}
              >
                {group.label}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${columnWidth}px, 1fr))`,
                  gap: 0,
                }}
              >
                {group.items.map((photo, index) => (
                  <div
                    key={`${photo.id}-${index}`}
                    data-photo-id={photo.id}
                    style={{
                      borderRadius: 0,
                      overflow: 'hidden',
                      border: 'none',
                      cursor: 'pointer',
                      background: '#fff',
                      contentVisibility: 'auto',
                      contain: 'layout paint',
                    }}
                    onClick={() => handlePhotoClick(photo.id)}
                  >
                    <div style={{ width: '100%', overflow: 'hidden' }}>
                      {(() => {
                        const thumbnailUrl = normalizeImageUrl(photo.thumbnailUrl ?? undefined);
                        const src = thumbnailUrl;
                        const isMissing = !src || failedThumbnails.has(photo.id);
                        const missingLabel =
                          photo.status === 'uploading' || photo.status === 'processing'
                            ? '処理中'
                            : '取得失敗';
                        if (isMissing) {
                          return (
                            <div
                              style={{
                                width: '100%',
                                paddingTop: '75%',
                                background: '#f5f5f5',
                                position: 'relative',
                              }}
                            >
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  color: '#8c8c8c',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  gap: 6,
                                  fontSize: 11,
                                }}
                              >
                                <FileImageOutlined style={{ fontSize: 18 }} />
                                <div>{missingLabel}</div>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <img
                            src={src}
                            alt={`Photo ${index + 1}`}
                            loading="lazy"
                            decoding="async"
                            onError={() => markThumbnailFailed(photo.id)}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                          />
                        );
                      })()}
                    </div>
                    <div style={{ padding: '6px 8px', fontSize: 12, color: '#595959' }}>
                      {formatTimeOnly(photo.captured_at || photo.timestamp)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div ref={loadMoreRef} style={{ padding: '12px 0', textAlign: 'center' }}>
            {(loading || loadingMore) && <Spin size="small" />}
            {canLoadMore && !(loading || loadingMore) && (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>下へスクロールで読み込み</div>
            )}
            {!hasMore && photos.length > 0 && (
              <div style={{ fontSize: 12, color: '#8c8c8c' }}>これ以上の写真はありません</div>
            )}
          </div>
        </div>

        <div
          ref={timelineContainerRef}
          onMouseMove={handleTimelineMouseMove}
          onMouseLeave={handleTimelineMouseLeave}
          style={{
            width: 140,
            flex: '0 0 140px',
            alignSelf: 'stretch',
            maxHeight: '100%',
            height: '100%',
            minHeight: 0,
            padding: '4px 16px',
            boxSizing: 'border-box',
            cursor: timelineIndicator?.isActive === false ? 'not-allowed' : TIMELINE_CURSOR,
          }}
        >
          <div
            style={{
              position: 'relative',
              height: timelineHeight ? `${timelineHeight}px` : '100%',
              minHeight: timelineHeight ? `${timelineHeight}px` : 0,
            }}
            onClick={handleTimelineClick}
          >
            <div
              style={{
                position: 'absolute',
                right: 34,
                top: 12,
                bottom: 12,
                width: 4,
                backgroundImage:
                  'radial-gradient(circle, rgba(0,0,0,0.18) 1.5px, transparent 1.6px)',
                backgroundRepeat: 'repeat-y',
                backgroundPosition: 'center top',
                backgroundSize: '4px 10px',
              }}
            />
            {timelineIndicator && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 34,
                  top: timelineIndicator.offset,
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    height: 0,
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 0,
                      height: 0,
                      background: 'transparent',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      right: 34,
                      left: 'auto',
                      top: 0,
                      transform: 'translateY(-50%)',
                      padding: '0 8px 2px 0',
                      background: '#ffffff',
                      zIndex: 20,
                      fontSize: 12,
                      fontWeight: 600,
                      color: timelineIndicator.isActive ? '#1f1f1f' : '#8c8c8c',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      borderBottom: `3px solid ${timelineIndicator.isActive ? '#7aa7ff' : 'rgba(0,0,0,0.2)'}`,
                      marginRight: -34,
                      boxShadow: '0 0 0 2px #ffffff',
                    }}
                  >
                    {timelineIndicator.isActive
                      ? timelineIndicator.label
                      : `${timelineIndicator.label}（写真なし）`}
                  </div>
                </div>
              </div>
            )}
            {timelineScale?.months.map((item, index) => {
              const totalMonths = timelineScale.months.length;
              const ratio = totalMonths > 1 ? index / (totalMonths - 1) : 0;
              const active = activeGroupKey ? activeGroupKey.startsWith(item.key) : false;
              const dotSize = item.count > 0 ? 8 : 5;
              const monthLabel = item.key.slice(5, 7);
              return (
                <div
                  key={item.key}
                  title={`${item.label} (${item.count}枚)`}
                  style={{
                    position: 'absolute',
                    right: 28,
                    top: `${ratio * 100}%`,
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      width: dotSize,
                      height: dotSize,
                      borderRadius: '50%',
                      background: active
                        ? '#1677ff'
                        : item.count > 0
                          ? '#8c8c8c'
                          : '#cfcfcf',
                      boxShadow: active ? '0 0 0 4px rgba(22,119,255,0.15)' : 'none',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      left: dotSize + 6,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 10,
                      color: 'rgba(0,0,0,0.45)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {monthLabel}
                  </div>
                </div>
              );
            })}
            {timelineScale?.yearMarkers.map((marker, index) => (
              <div
                key={`${marker.label}-${index}`}
                style={{
                  position: 'absolute',
                  right: 44,
                  left: 'auto',
                  top: `${marker.ratio * 100}%`,
                  transform: 'translateY(-50%)',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#1f1f1f',
                  pointerEvents: 'none',
                  textAlign: 'right',
                  zIndex: 1,
                }}
              >
                {marker.label}
              </div>
            ))}
            {!timelineScale && (
              <div style={{ fontSize: 12, color: '#8c8c8c', textAlign: 'right' }}>
                日付情報なし
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal
        open={selectedPhoto !== null}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        centered
        closeIcon={<CloseOutlined />}
      >
        <div style={{ position: 'relative' }}>
          {selectedPhoto && (
            <>
              <Button
                aria-label="前の写真へ"
                shape="circle"
                icon={<LeftOutlined />}
                disabled={!hasPrev}
                onClick={handlePrev}
                style={{
                  position: 'absolute',
                  left: -8,
                  top: '40%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              />
              <Button
                aria-label="次の写真へ"
                shape="circle"
                icon={<RightOutlined />}
                disabled={!hasNext}
                onClick={handleNext}
                style={{
                  position: 'absolute',
                  right: -8,
                  top: '40%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              />
            </>
          )}

          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>詳細情報を読み込み中...</div>
            </div>
          ) : photoDetail ? (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                {fullImageUrl ? (
                  <img
                    src={fullImageUrl}
                    alt="Full size photo"
                    style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin />
                    <span style={{ marginLeft: 8 }}>画像を読み込み中...</span>
                  </div>
                )}
              </div>

              {(() => {
                const latitude = photoDetail.gps_latitude;
                const longitude = photoDetail.gps_longitude;
                const hasGps = typeof latitude === 'number' && typeof longitude === 'number';
                const heading =
                  typeof photoDetail.imu_orientation === 'number'
                    ? photoDetail.imu_orientation
                    : typeof photoDetail.direction === 'number'
                      ? photoDetail.direction
                      : null;
                return (
                  <>
                <Descriptions title="写真情報" bordered column={2} size="small">
                  <Descriptions.Item label="撮影日時">
                    {formatTimestamp(photoDetail.captured_at)}
                  </Descriptions.Item>
                  <Descriptions.Item label="ファイルサイズ">
                    {(photoDetail.file_size / 1024 / 1024).toFixed(2)} MB
                  </Descriptions.Item>

                  {photoDetail.image_width && (
                    <Descriptions.Item label="解像度">
                      {photoDetail.image_width} x {photoDetail.image_height}
                    </Descriptions.Item>
                  )}

                  {photoDetail.camera_make && (
                    <Descriptions.Item label="カメラ">
                      {photoDetail.camera_make} {photoDetail.camera_model}
                    </Descriptions.Item>
                  )}

                  {photoDetail.iso_speed && (
                    <Descriptions.Item label="ISO">
                      {photoDetail.iso_speed}
                    </Descriptions.Item>
                  )}

                  {photoDetail.aperture_value && (
                    <Descriptions.Item label="絞り">
                      F/{photoDetail.aperture_value}
                    </Descriptions.Item>
                  )}

                  {photoDetail.exposure_time && (
                    <Descriptions.Item label="シャッター速度">
                      1/{Math.round(1 / photoDetail.exposure_time)}秒
                    </Descriptions.Item>
                  )}

                  {photoDetail.focal_length && (
                    <Descriptions.Item label="焦点距離">
                      {photoDetail.focal_length}mm
                    </Descriptions.Item>
                  )}

                  {hasGps && (
                    <Descriptions.Item label="GPS座標" span={2}>
                      <EnvironmentOutlined /> {latitude!.toFixed(6)}, {longitude!.toFixed(6)}
                      {typeof photoDetail.gps_altitude === 'number' && ` (高度: ${photoDetail.gps_altitude.toFixed(1)}m)`}
                    </Descriptions.Item>
                  )}

                  {(typeof photoDetail.imu_pitch === 'number' ||
                    typeof photoDetail.imu_roll === 'number' ||
                    typeof photoDetail.imu_orientation === 'number') && (
                    <Descriptions.Item label="IMU情報" span={2}>
                      {typeof photoDetail.imu_pitch === 'number' && `ピッチ: ${photoDetail.imu_pitch.toFixed(1)}° `}
                      {typeof photoDetail.imu_roll === 'number' && `ロール: ${photoDetail.imu_roll.toFixed(1)}° `}
                      {typeof photoDetail.imu_orientation === 'number' && `方位: ${photoDetail.imu_orientation.toFixed(1)}°`}
                    </Descriptions.Item>
                  )}
                </Descriptions>
                {hasGps && (
                  <div style={{ marginTop: 16 }}>
                    <PhotoLocationMap latitude={latitude!} longitude={longitude!} heading={heading} />
                  </div>
                )}
                <div style={{ marginTop: 16 }}>
                  <Card
                    size="small"
                    title="顔検出"
                    extra={
                      <Button
                        size="small"
                        onClick={handleFaceDetectionRetry}
                        loading={faceDetectionSubmitting}
                        disabled={faceDetectionLoading || faceDetectionSubmitting || isFaceDetectionRunning}
                      >
                        顔検出を再実行
                      </Button>
                    }
                  >
                    {faceDetectionLoading ? (
                      <div style={{ textAlign: 'center', padding: 16 }}>
                        <Spin />
                      </div>
                    ) : (
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Text type="secondary">ステータス: {faceStatusLabel(faceDetection?.status)}</Text>
                        {typeof faceDetection?.progress === 'number' && (
                          <Text type="secondary">
                            進捗: {(faceDetection.progress * 100).toFixed(1)}%
                          </Text>
                        )}
                        {embeddingDim !== null && (
                          <Text type="secondary">Embedding: {embeddingDim}次元（生成済み）</Text>
                        )}
                        {faceDetectionError && <Text type="danger">{faceDetectionError}</Text>}
                        {faceDetection?.error_message && (
                          <Text type="danger">{faceDetection.error_message}</Text>
                        )}
                        {faceDetection?.status === 'succeeded' && (
                          <>
                            {faceDetection.faces && faceDetection.faces.length > 0 ? (
                              <Row gutter={[8, 8]}>
                                {faceDetection.faces.map((face, index) => (
                                  <Col xs={12} sm={8} md={6} key={`${face.face_photo_id ?? index}`}>
                                    <div style={{ textAlign: 'center' }}>
                                      {face.image_url ? (
                                        <Image
                                          src={face.image_url}
                                          alt={`face-${index}`}
                                          width="100%"
                                          style={{ borderRadius: 6, objectFit: 'cover' }}
                                          preview
                                        />
                                      ) : (
                                        <div style={{ padding: 12 }}>
                                          <Text type="secondary">画像なし</Text>
                                        </div>
                                      )}
                                      {typeof face.score === 'number' && (
                                        <Text type="secondary">検出信頼度: {face.score.toFixed(2)}</Text>
                                      )}
                                    </div>
                                  </Col>
                                ))}
                              </Row>
                            ) : (
                              <Text type="secondary">顔は検出されませんでした</Text>
                            )}
                          </>
                        )}
                      </Space>
                    )}
                  </Card>
                </div>
                  </>
                );
              })()}
            </div>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default PhotoGallery;
export { PhotoGallery };
